import React from 'react';
import classnames from 'classnames';
import {
	getBeginNext,
	getBeginNext2,
	getBeginPrevious,
	getBeginPrevious2,
	getEndPrevious,
	getEndPrevious2,
} from './shared/dates';
import { formatMonthYear as defaultFormatMonthYear } from './shared/dateFormatter';
import type { Action, NavigationLabelFunc } from './shared/types';
import { getYear, isAfter, isEqual, isBefore, isSameMonth } from 'date-fns';

const className = 'FSHCalendarView__navigation';

type NavigationProps = {
	activeStartDate: Date;
	formatMonthYear?: typeof defaultFormatMonthYear;
	locale: string;
	maxDate?: Date;
	minDate?: Date;
	navigationAriaLabel?: string | ((activeStartDate: Date, showDoubleView?: boolean) => string);
	navigationAriaLive?: 'off' | 'polite' | 'assertive';
	navigationLabel?: NavigationLabelFunc;
	next2AriaLabel?: string;
	next2Label?: React.ReactNode;
	nextAriaLabel?: string;
	nextLabel?: React.ReactNode;
	prev2AriaLabel?: string;
	prev2Label?: React.ReactNode;
	prevAriaLabel?: string;
	prevLabel?: React.ReactNode;
	setActiveStartDate: (nextActiveStartDate: Date, action: Action) => void;
	showDoubleView?: boolean;
};

export default function Navigation({
	activeStartDate,
	formatMonthYear = defaultFormatMonthYear,
	locale,
	maxDate,
	minDate,
	navigationAriaLabel = '',
	navigationAriaLive,
	navigationLabel,
	next2AriaLabel = '',
	next2Label = '»',
	nextAriaLabel = '',
	nextLabel = '›',
	prev2AriaLabel = '',
	prev2Label = '«',
	prevAriaLabel = '',
	prevLabel = '‹',
	setActiveStartDate,
	showDoubleView,
}: NavigationProps) {
	const previousActiveStartDate = getBeginPrevious(activeStartDate);
	const previousActiveStartDate2 = getBeginPrevious2(activeStartDate);
	const nextActiveStartDate = getBeginNext(activeStartDate);
	const nextActiveStartDate2 = getBeginNext2(activeStartDate);

	const prevButtonDisabled = (() => {
		if (getYear(previousActiveStartDate) < 0) {
			return true;
		}
		const previousActiveEndDate = getEndPrevious(activeStartDate);
		return minDate
			? isAfter(minDate, previousActiveEndDate) || isEqual(minDate, previousActiveEndDate)
			: false;
	})();

	const prev2ButtonDisabled = (() => {
		if (getYear(previousActiveStartDate2) < 0) {
			return true;
		}
		const previousActiveEndDate = getEndPrevious2(activeStartDate);
		return minDate
			? isAfter(minDate, previousActiveEndDate) || isEqual(minDate, previousActiveEndDate)
			: false;
	})();

	const nextButtonDisabled = maxDate
		? isBefore(maxDate, nextActiveStartDate) || isSameMonth(maxDate, nextActiveStartDate)
		: false;

	const next2ButtonDisabled = maxDate
		? isBefore(maxDate, nextActiveStartDate) || isSameMonth(maxDate, nextActiveStartDate)
		: false;

	function onClickPrevious() {
		setActiveStartDate(previousActiveStartDate, 'prev');
	}

	function onClickPrevious2() {
		setActiveStartDate(previousActiveStartDate2, 'prev2');
	}

	function onClickNext() {
		setActiveStartDate(nextActiveStartDate, 'next');
	}

	function onClickNext2() {
		setActiveStartDate(nextActiveStartDate2, 'next2');
	}

	return (
		<div className={className}>
			<NavigationButton
				ariaLabel={prev2AriaLabel}
				label={prev2Label}
				onClick={onClickPrevious2}
				disabled={prev2ButtonDisabled}
				direction="prev2"
			/>
			<NavigationButton
				ariaLabel={prevAriaLabel}
				label={prevLabel}
				onClick={onClickPrevious}
				disabled={prevButtonDisabled}
				direction="prev"
			/>
			<NavigationHeader
				activeStartDate={activeStartDate}
				ariaLabel={
					typeof navigationAriaLabel === 'string'
						? navigationAriaLabel
						: (navigationAriaLabel?.(activeStartDate, showDoubleView) ?? '')
				}
				ariaLive={navigationAriaLive}
				formatMonthYear={formatMonthYear}
				label={navigationLabel}
				locale={locale}
				nextActiveStartDate={nextActiveStartDate}
				showDoubleView={showDoubleView}
			/>
			<NavigationButton
				ariaLabel={nextAriaLabel}
				label={nextLabel}
				onClick={onClickNext}
				disabled={nextButtonDisabled}
				direction="next"
			/>
			<NavigationButton
				ariaLabel={next2AriaLabel}
				label={next2Label}
				onClick={onClickNext2}
				disabled={next2ButtonDisabled}
				direction="next2"
			/>
		</div>
	);
}

type NavigationHeaderProps = {
	activeStartDate: NavigationProps['activeStartDate'];
	ariaLabel?: string;
	ariaLive?: NavigationProps['navigationAriaLive'];
	formatMonthYear?: NavigationProps['formatMonthYear'];
	label?: NavigationProps['navigationLabel'];
	locale: string;
	nextActiveStartDate?: Date;
	showDoubleView?: NavigationProps['showDoubleView'];
};

function NavigationHeader({
	activeStartDate,
	ariaLabel,
	ariaLive,
	formatMonthYear = defaultFormatMonthYear,
	label,
	locale,
	nextActiveStartDate,
	showDoubleView,
}: NavigationHeaderProps) {
	const labelClassName = `${className}__label`;

	function renderLabel(date: Date) {
		const formatted = (() => {
			return formatMonthYear(locale, date);
		})();

		return label
			? label({
					date,
					label: formatted,
					locale: locale,
				})
			: formatted;
	}

	return (
		<p aria-label={ariaLabel} aria-live={ariaLive} className={labelClassName}>
			<strong
				className={`${labelClassName}__labelText ${labelClassName}__labelText--from CalendarMonth_caption`}
			>
				{renderLabel(activeStartDate)}
			</strong>
			{showDoubleView ? (
				<strong
					className={`${labelClassName}__labelText ${labelClassName}__labelText--to CalendarMonth_caption`}
				>
					{renderLabel(nextActiveStartDate ?? getBeginNext(activeStartDate))}
				</strong>
			) : null}
		</p>
	);
}

type NavigationButtonProps = {
	ariaLabel?: string;
	label?: React.ReactNode;
	direction?: string;
	buttonRef?: React.RefObject<HTMLButtonElement>;
} & React.ComponentPropsWithRef<'button'>;

function NavigationButton({
	ariaLabel,
	direction,
	label,
	disabled,
	buttonRef,
	...props
}: NavigationButtonProps) {
	if (disabled) {
		props['aria-disabled'] = disabled;
		props.onClick = undefined;
	}

	return label ? (
		<button
			{...props}
			aria-label={ariaLabel}
			className={classnames({
				[`${className}__arrow`]: true,
				[`${className}__${direction}-button`]: direction,
			})}
			type="button"
			ref={buttonRef}
		>
			{label}
		</button>
	) : null;
}
